/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApiManager from "../../../../Services/ApiManager/ApiManager";

export default function AddVehicleDialog({
  open,
  onClose,
  onSubmit,
  onError,
  portfolioId,
  vehicleListId,
}) {
  const [vehicleId, setVehicleId] = useState("");
  const [friendlyName, setFriendlyName] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!vehicleId.trim()) {
      newErrors.vehicleId = "Vehicle ID is required";
    }

    // Friendly Name is now optional - no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const extractErrorMessage = (error) => {
    const defaultMessage = "Failed to add vehicle. Please try again.";

    if (!error?.response?.data) {
      return error?.message || defaultMessage;
    }

    const errorData = error.response.data;
    console.log("Error data structure:", errorData, "Type:", typeof errorData);

    // Handle array format: [{"message":"Member 123123 doesn't exist"}]
    if (Array.isArray(errorData) && errorData.length > 0) {
      return errorData[0]?.message || errorData[0];
    }

    // Handle object format with message property
    if (errorData?.message) {
      return errorData.message;
    }

    // Handle string format
    if (typeof errorData === "string") {
      return errorData;
    }

    // Handle other object formats that might contain error text
    return errorData?.error || defaultMessage;
  };

  const handleSubmit = async () => {
    if (isFormValid && validateForm()) {
      try {
        setLoading(true);
        const payload = {
          id: vehicleId.trim(),
          description: friendlyName.trim() || "", // Use empty string if no friendly name provided
        };

        await ApiManager.addVehicleToGroup(portfolioId, vehicleListId, payload);

        // Call the parent's onSubmit with the vehicle data for local state update
        onSubmit({
          vehicleId: vehicleId.trim(),
          friendlyName: friendlyName.trim(),
        });

        handleClose();
      } catch (error) {
        console.error("Error adding vehicle:", error);
        console.error("Error response:", error?.response?.data);

        const errorMessage = extractErrorMessage(error);
        console.log("Final error message:", errorMessage);

        // Pass error to parent for toast display
        if (onError) {
          onError(`Error: ${errorMessage}`);
        }

        // Don't close dialog on error, let user retry
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setVehicleId("");
    setFriendlyName("");
    setErrors({});
    onClose();
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && isFormValid) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const isFormValid = vehicleId.trim(); // Only Vehicle ID is required

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            maxWidth: "500px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          Add Vehicle
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: "#666",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#333",
                marginBottom: "8px",
              }}
            >
              Vehicle VIN <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Vehicle ID 123456"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              onKeyDown={handleKeyPress}
              error={!!errors.vehicleId}
              helperText={errors.vehicleId}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  padding: "12px 14px",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#999",
                  opacity: 1,
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#333",
                marginBottom: "8px",
              }}
            >
              Friendly Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Friendly Name"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              onKeyDown={handleKeyPress}
              error={!!errors.friendlyName}
              helperText={errors.friendlyName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  padding: "12px 14px",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#999",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          gap: 2,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: 1,
            borderColor: "#e0e0e0",
            color: "#666",
            "&:hover": {
              backgroundColor: "#f5f5f5",
              borderColor: "#d0d0d0",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: 1,
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            "&:disabled": {
              backgroundColor: "#e0e0e0",
              color: "#999",
            },
          }}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddVehicleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  portfolioId: PropTypes.string.isRequired,
  vehicleListId: PropTypes.string.isRequired,
};
